import type { WeightUnit } from './types';

/** The international avoirdupois pound. */
export const GRAMS_PER_LB = 453.59237;
/** Any adult weight a bathroom scale shows: 20 to 400 kg. */
export const MIN_WEIGHT_G = 20_000;
export const MAX_WEIGHT_G = 400_000;

/** A typed value (in kg or lb) to whole grams. */
export function toGrams(value: number, unit: WeightUnit): number {
  return Math.round(unit === 'kg' ? value * 1000 : value * GRAMS_PER_LB);
}

/** Grams to the display unit, rounded to one decimal (72400 g -> 72.4 kg, 159.6 lb). */
export function fromGrams(grams: number, unit: WeightUnit): number {
  const raw = unit === 'kg' ? grams / 1000 : grams / GRAMS_PER_LB;
  return Math.round(raw * 10) / 10;
}

/** "72.4" (always one decimal, no unit). */
export function weightNumber(grams: number, unit: WeightUnit): string {
  return fromGrams(grams, unit).toFixed(1);
}

/** "72.4 kg". */
export function formatWeight(grams: number, unit: WeightUnit): string {
  return `${weightNumber(grams, unit)} ${unit}`;
}

/** A change with its sign: "−0.6 kg", "+1.2 lb", "0.0 kg". */
export function formatWeightChange(grams: number, unit: WeightUnit): string {
  const v = fromGrams(Math.abs(grams), unit);
  const sign = v === 0 ? '' : grams < 0 ? '−' : '+';
  return `${sign}${v.toFixed(1)} ${unit}`;
}

export type ParseWeightError = 'empty' | 'not_a_number' | 'out_of_range';

/** What the user typed ("72.4", "72,4", " 160 ") to grams, within 20–400 kg. */
export function parseWeight(
  text: string,
  unit: WeightUnit,
): { ok: true; grams: number } | { ok: false; error: ParseWeightError } {
  const clean = text.trim().replace(',', '.');
  if (clean === '') return { ok: false, error: 'empty' };
  if (!/^\d{1,4}(\.\d{0,2})?$/.test(clean)) return { ok: false, error: 'not_a_number' };
  const grams = toGrams(Number(clean), unit);
  if (grams < MIN_WEIGHT_G || grams > MAX_WEIGHT_G) return { ok: false, error: 'out_of_range' };
  return { ok: true, grams };
}

export function isValidGrams(grams: unknown): grams is number {
  return (
    typeof grams === 'number' &&
    Number.isInteger(grams) &&
    grams >= MIN_WEIGHT_G &&
    grams <= MAX_WEIGHT_G
  );
}
